import { ReactNode } from 'react';

import Link from 'next/link';

import Typography from '@components/Typography';
import { useDevice } from '@contexts/device';
import { tourYouLike } from '@utils/newDataLayer/productYouLike';
import ImageWithFallback from '@components/utils/ImageWithFallback';
import { getCurrencySymbol } from '@utils/utils';
import { Separator } from '@components/formStyles';
import { TourRecommendationSearchViewModel } from '@api/v3';
import { cloudineryImage } from '@components/views/hotel/detail/utils/features';
import DiscountLabel from '@components/ui/labels/discount/DiscountLabel';

import {
  ImageDiv,
  NextCarouselImgWrapper,
  Image,
  NameBodyWrapper,
  RegionWrapper,
  ShowTourButton,
  ShowTourButtonWrapper,
  CarouselWrapper,
} from './styled';
import Carousel, { SwiperSlide } from './CarouselItem';
import PriceLabel from './price/PriceLabel';

type ICarouselProps = {
  data: TourRecommendationSearchViewModel[];
  earliestDepartureDate?: string;
  isExpedia?: boolean;
  setIsRedirecting?: (value: boolean) => void;
};

const CarouselCardLink = ({
  url,
  children,
  linkTitle,
  earliestDepartureDate,
}: {
  children: ReactNode;
  url: string;
  linkTitle?: string;
  earliestDepartureDate?: string;
}) => {
  return (
    <>
      {url ? (
        <Link
          title={linkTitle || ''}
          href={
            earliestDepartureDate
              ? `${url}?date=${earliestDepartureDate}`
              : `${url}`
          }
          prefetch={false}
        >
          {children}
        </Link>
      ) : (
        children
      )}
    </>
  );
};

const CarouselCard = ({
  data,
  isExpedia,
  earliestDepartureDate,
  setIsRedirecting,
}: ICarouselProps) => {
  const { isDesktop } = useDevice();

  return (
    <CarouselWrapper>
      <Carousel>
        {data?.slice(0, 10)?.map((item) =>
          item.name ? (
            <SwiperSlide key={item.id}>
              <NextCarouselImgWrapper>
                <ImageDiv>
                  <CarouselCardLink
                    url={item?.webUrl || ''}
                    linkTitle={item?.name || ''}
                    earliestDepartureDate={earliestDepartureDate}
                  >
                    <ImageWithFallback
                      Component={Image}
                      cursor={!!item.webUrl}
                      onClick={() => {
                        setIsRedirecting?.(true);
                        tourYouLike([item], true);
                      }}
                      src={cloudineryImage(item.image, 270, 200) || ''}
                      alt={item?.name || 'setur'}
                      fill
                    />
                  </CarouselCardLink>
                </ImageDiv>
                <NameBodyWrapper>
                  <RegionWrapper>
                    {item.transportType ? (
                      <Typography
                        variant='caption2'
                        weight='Regular'
                        color='NL300'
                        as='div'
                        style={{ height: '18px' }}
                      >
                        {item.transportType}
                      </Typography>
                    ) : null}
                  </RegionWrapper>
                  <CarouselCardLink
                    url={item?.webUrl || ''}
                    linkTitle={item?.name || ''}
                    earliestDepartureDate={earliestDepartureDate}
                  >
                    <Typography
                      variant={isDesktop ? 'caption' : 'Title5'}
                      weight='SemiBold'
                      color='NL800'
                      as='div'
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '260px',
                      }}
                    >
                      {item.name}
                    </Typography>
                  </CarouselCardLink>
                </NameBodyWrapper>
                {item.rate && !isExpedia ? (
                  <DiscountLabel discount={item.rate} type='percent' />
                ) : (
                  <Separator size='26px' />
                )}
                <ShowTourButtonWrapper>
                  {item.price && !isExpedia ? (
                    <div>
                      <PriceLabel
                        priceWithDiscount={item.discountedPrice}
                        currency={getCurrencySymbol('TL')}
                        originalPrice={item.price}
                        rate={item.rate}
                      />
                      <Typography
                        variant='caption3'
                        weight='Regular'
                        color='NL500'
                        as='div'
                        style={{ height: '12px' }}
                      >
                        'den itibaren
                      </Typography>
                    </div>
                  ) : (
                    <div />
                  )}
                  <CarouselCardLink
                    url={item?.webUrl || ''}
                    linkTitle={item?.name || ''}
                    earliestDepartureDate={earliestDepartureDate}
                  >
                    <ShowTourButton
                      onClick={() => {
                        setIsRedirecting?.(true);
                        tourYouLike([item], true);
                      }}
                    >
                      <Typography
                        variant='caption2'
                        weight='Bold'
                        color='persianRed'
                      >
                        Turu Gör
                      </Typography>
                    </ShowTourButton>
                  </CarouselCardLink>
                </ShowTourButtonWrapper>
              </NextCarouselImgWrapper>
            </SwiperSlide>
          ) : null
        )}
      </Carousel>
    </CarouselWrapper>
  );
};

export default CarouselCard;
